import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { NoteListContainer, NotesCollapseButton, NoteItem } from "../components";
import { deleteTaskNote } from "../apis/deleteTaskNote";
import { filter } from "lodash";
import { translate } from "@td/shared_utils";
import toastr from "../../toastr";

const deleteConfirmationNotification = translate(null, "care_team_tasks.form.fields.notes", "delete_confirmation");

const NoteList = React.memo(({ notes }) => {
  const [deletedTaskNotes, setDeletedTaskNotes] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const filteredNotes = useMemo(() => {
    return filter(notes, ({ id }) => !deletedTaskNotes.includes(id));
  }, [notes, deletedTaskNotes]);

  const handleDelete = useCallback(async (taskNoteId) => {
    const isConfirmed = confirm(
      deleteConfirmationNotification
    );

    if(!isConfirmed) return;

    const { success } = await deleteTaskNote(taskNoteId);
    if (success) {
      toastr.success(
        translate(null, "care_team_tasks.notifications", "task_note_deleted")
      );

      setDeletedTaskNotes([
        taskNoteId,
        ...deletedTaskNotes,
      ]);
    }
  }, [deletedTaskNotes, setDeletedTaskNotes]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed, setIsCollapsed]);

  useEffect(() => {
    if (filteredNotes.length <= 1) {
      setIsCollapsed(true);
    }
  }, [filteredNotes, setIsCollapsed]);

  if (!filteredNotes.length) {
    return null;
  }

  return (
    <NoteListContainer>
      {filteredNotes.slice(0, 1).map((note) => (
        <NoteItem
          key={note}
          note={note}
          onDelete={handleDelete}
        />
      ))}

      {filteredNotes.length > 1 && (
        <NotesCollapseButton
          isCollapsed={isCollapsed}
          onClick={toggleCollapse}
        />
      )}

      {!isCollapsed && (
        <React.Fragment>
          {filteredNotes.slice(1).map((note) => (
            <NoteItem
              key={note}
              note={note}
              onDelete={handleDelete}
            />
          ))}
        </React.Fragment>
      )}
    </NoteListContainer>
  );
});

NoteList.propTypes = {
  notes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    author: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    note: PropTypes.string.isRequired,
    noteCreationDt: PropTypes.string.isRequired,
  })),
};

export default NoteList;
